import { useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { i18next, useTranslation } from '@frontend/i18n';
import {
  TimeRangeField,
  Table,
  TableColumnConfig,
  Text,
  CheckboxField,
  FieldProps,
  FormValues,
} from '@frontend/design-system';
import { BulkOverrideWarningAlert } from '../../common/bulk-override-warning-alert';
import { DEFAULT_TIME_RANGE } from '../constants';
import {
  BusinessKnowledgeFormFields,
  getHoursCheckboxValue,
  getHoursTimeRangeValue,
} from './use-business-knowledge-form';

type Props = {
  getFieldProps: <K extends keyof BusinessKnowledgeFormFields>(name: K) => FieldProps<BusinessKnowledgeFormFields, K>;
  values: FormValues<BusinessKnowledgeFormFields>;
  showBulkOverrideWarningAlert?: boolean;
};

const DAYS: ScheduleAvailabilityTypes.Weekday[] = [
  ScheduleAvailabilityTypes.Weekday.monday,
  ScheduleAvailabilityTypes.Weekday.tuesday,
  ScheduleAvailabilityTypes.Weekday.wednesday,
  ScheduleAvailabilityTypes.Weekday.thursday,
  ScheduleAvailabilityTypes.Weekday.friday,
  ScheduleAvailabilityTypes.Weekday.saturday,
  ScheduleAvailabilityTypes.Weekday.sunday,
];

const WeekdayLabelMap: Record<ScheduleAvailabilityTypes.Weekday, string> = {
  [ScheduleAvailabilityTypes.Weekday.monday]: i18next.t('Monday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.tuesday]: i18next.t('Tuesday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.wednesday]: i18next.t('Wednesday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.thursday]: i18next.t('Thursday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.friday]: i18next.t('Friday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.saturday]: i18next.t('Saturday', { ns: 'ai-receptionist-settings' }),
  [ScheduleAvailabilityTypes.Weekday.sunday]: i18next.t('Sunday', { ns: 'ai-receptionist-settings' }),
};

type RowData = {
  day: ScheduleAvailabilityTypes.Weekday;
  dayName: string;
  isOpen: boolean;
  timeRange: { start: string; end: string };
};

export const BusinessHoursInput = ({ getFieldProps, values, showBulkOverrideWarningAlert = false }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const tableData: RowData[] = useMemo(() => {
    return DAYS.map((day) => {
      const isOpen = getHoursCheckboxValue(values, day);
      const [start, end] = getHoursTimeRangeValue(values, day);
      const timeRange = start && end ? { start, end } : DEFAULT_TIME_RANGE;

      return {
        day,
        dayName: WeekdayLabelMap[day],
        isOpen,
        timeRange,
      };
    });
  }, [values]);

  const colConfig: TableColumnConfig<RowData>[] = useMemo(() => {
    const cols: TableColumnConfig<RowData>[] = [];

    cols.push({
      id: 'checkbox',
      Header: '',
      accessor: (row) => row.isOpen,
      cellRenderer: (_: unknown, rowData?: RowData) => {
        if (!rowData) return null;
        const checkboxProps = getFieldProps(`${rowData.day}-checkbox`);
        return <CheckboxField {...checkboxProps} label='' />;
      },
      width: 48,
      disableSortBy: true,
    });

    cols.push({
      id: 'day',
      Header: t('Day'),
      accessor: (row) => row.dayName,
      cellRenderer: (_: unknown, rowData?: RowData) => {
        if (!rowData) return null;
        return <Text size='medium'>{rowData.dayName}</Text>;
      },
      maxWidth: 160,
      disableSortBy: true,
    });

    cols.push({
      id: 'hours',
      Header: t('Hours'),
      disableSortBy: true,
      accessor: (row) => {
        return row.isOpen
          ? t('{{start}} to {{end}}', { start: row.timeRange.start ?? '-', end: row.timeRange.end ?? '-' })
          : t('Closed');
      },
      cellRenderer: (_: unknown, rowData?: RowData) => {
        if (!rowData) return null;

        if (!rowData.isOpen) {
          return <input css={styles.closedInput} disabled value={t('Closed')} />;
        }

        const timeRangeProps = getFieldProps(`${rowData.day}-time-range`);
        return <TimeRangeField {...timeRangeProps} joiningText={t('to')} label='' />;
      },
    });

    return cols;
  }, [t, getFieldProps]);

  return (
    <>
      <div css={styles.tableWrapper}>
        <Table colConfig={colConfig} data={tableData} />
      </div>
      {showBulkOverrideWarningAlert && <BulkOverrideWarningAlert />}
    </>
  );
};

const styles = {
  tableWrapper: css`
    background-color: var(--fabric-color-surface-default);
    border-radius: var(--fabric-radius-sm);
    overflow: auto;
  `,
  closedInput: css`
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
    border: var(--fabric-border-width-thin) solid var(--fabric-color-accent-neutral-muted);
    border-radius: var(--fabric-radius-sm);
    background-color: var(--fabric-color-surface-default);
    color: var(--fabric-color-text-subdued);
    font-size: var(--fabric-font-size-medium);
    width: 140px;
    box-sizing: border-box;
  `,
};
